import React, { useState, useEffect } from 'react';
import { X, Share, Plus, Smartphone, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

interface IOSPWAInstallGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

const IOSPWAInstallGuide: React.FC<IOSPWAInstallGuideProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const checkIOSDevice = () => {
      return /iPad|iPhone|iPod/.test(navigator.userAgent);
    };

    const checkStandalone = () => {
      return window.matchMedia('(display-mode: standalone)').matches || 
             (window.navigator as any).standalone === true;
    };

    setIsIOSDevice(checkIOSDevice());
    setIsStandalone(checkStandalone());
  }, []);

  const steps = [
    {
      title: "1. Abra no Safari",
      description: "Certifique-se de que está usando o Safari (não Chrome ou outros navegadores)",
      icon: <Smartphone className="w-8 h-8 text-blue-500" />,
      detail: "O iOS só suporta notificações PWA através do Safari. Outros navegadores não funcionam."
    },
    {
      title: "2. Toque em Compartilhar",
      description: "Toque no ícone de compartilhar na barra inferior do Safari",
      icon: <Share className="w-8 h-8 text-green-500" />,
      detail: "Procure pelo ícone de seta apontando para cima na parte inferior da tela."
    },
    {
      title: "3. Adicionar à Tela de Início",
      description: "Selecione 'Adicionar à Tela de Início' no menu",
      icon: <Plus className="w-8 h-8 text-purple-500" />,
      detail: "Role para baixo no menu de compartilhamento até encontrar esta opção."
    },
    {
      title: "4. Abra pela Tela Inicial",
      description: "Use o ícone criado na tela inicial para abrir o app",
      icon: <Bell className="w-8 h-8 text-orange-500" />,
      detail: "Apenas quando aberto pela tela inicial as notificações funcionarão corretamente."
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 safe-area-inset">
      <Card className="w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto">
        <CardHeader className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-2 top-2"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
          
          <CardTitle className="text-center">
            {isStandalone ? (
              <span className="text-green-600">✅ PWA Instalado!</span>
            ) : (
              <span className="text-orange-600">📱 Como Instalar no iOS</span>
            )}
          </CardTitle>
          
          <CardDescription className="text-center">
            {isStandalone ? (
              "Seu PWA está configurado corretamente para notificações!"
            ) : (
              "Siga os passos abaixo para instalar o app no seu iPhone"
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {isStandalone ? (
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <Bell className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-green-700">
                  Perfeito! Agora você pode receber notificações push.
                </p>
              </div>
              
              <div className="text-xs text-gray-600 space-y-1">
                <p><strong>Versão iOS:</strong> {navigator.userAgent.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Desconhecida'}</p>
                <p><strong>Modo Standalone:</strong> ✅ Ativo</p>
                <p><strong>Suporte a Notificações:</strong> {'userActivation' in navigator ? '✅ Disponível' : '❌ Não disponível'}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border transition-all ${
                    index === currentStep 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                  onClick={() => setCurrentStep(index)}
                >
                  <div className="flex items-start space-x-3">
                    {step.icon}
                    <div className="flex-1">
                      <h3 className="font-medium text-sm">{step.title}</h3>
                      <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                      {index === currentStep && (
                        <p className="text-xs text-blue-600 mt-2 font-medium">
                          💡 {step.detail}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-700">
                  <strong>⚠️ Importante:</strong> Notificações só funcionam no iOS 16.4+ e quando o PWA é aberto pela tela inicial.
                </p>
              </div>
            </div>
          )}

          <div className="flex space-x-2 mt-6">
            {!isStandalone && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                  disabled={currentStep === 0}
                  className="flex-1"
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
                  disabled={currentStep === steps.length - 1}
                  className="flex-1"
                >
                  Próximo
                </Button>
              </>
            )}
            <Button
              variant={isStandalone ? "default" : "secondary"}
              size="sm"
              onClick={onClose}
              className={isStandalone ? "w-full" : "flex-1"}
            >
              {isStandalone ? "Entendi!" : "Fechar"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IOSPWAInstallGuide;